<template>
  <div v-if="!requestId" class="flex items-center justify-center h-64 text-gray-400">
    Select a request to view its details.
  </div>
  <div v-else-if="loading" class="flex items-center justify-center h-64">
    <ProgressSpinner style="width: 36px; height: 36px" />
  </div>
  <div v-else-if="!request" class="text-sm text-gray-400 text-center py-6">
    Request not found.
  </div>
  <div v-else class="space-y-5">
    <!-- Header -->
    <div class="bg-white rounded-lg shadow p-5 flex flex-wrap items-start justify-between gap-3">
      <div>
        <div class="flex items-center gap-3">
          <h3 class="text-lg font-semibold">{{ request.applicant_name }}</h3>
          <TranscriptStatusBadge :status="request.status" />
        </div>
        <div class="text-sm text-gray-500 mt-1">
          <span class="font-mono">{{ request.tracking_code }}</span>
          <span class="mx-2">·</span>
          <span>{{ request.transcriptType?.name }}</span>
          <TranscriptCategoryBadge :category="request.transcriptType?.category" class="ml-2" />
        </div>
      </div>

      <!-- Action buttons (status-aware) -->
      <div class="flex flex-wrap items-center gap-2">
        <Button
          v-if="canLinkStudent"
          icon="pi pi-link"
          label="Link Student"
          size="small"
          severity="secondary"
          data-testid="action-link-student"
          @click="linkOpen = true"
        />
        <Button
          v-if="canRegenerate"
          icon="pi pi-refresh"
          label="Regenerate Data"
          size="small"
          severity="secondary"
          :loading="regenerating"
          :disabled="regenerating"
          data-testid="action-regenerate"
          @click="onRegenerate"
        />
        <Button
          v-if="hasGeneratedData"
          icon="pi pi-pencil"
          label="Cover Letter"
          size="small"
          severity="secondary"
          data-testid="action-cover-letter"
          @click="coverOpen = true"
        />
        <Button
          v-if="canApprove"
          icon="pi pi-check"
          label="Approve"
          size="small"
          severity="success"
          :loading="approving"
          :disabled="approving || !hasGeneratedData"
          v-tooltip.top="!hasGeneratedData ? 'Generate transcript data first' : ''"
          data-testid="action-approve"
          @click="onApprove"
        />
        <Button
          v-if="canReject"
          icon="pi pi-times"
          label="Reject"
          size="small"
          severity="danger"
          data-testid="action-reject"
          @click="rejectOpen = true"
        />
        <Button
          v-if="canDispatch"
          icon="pi pi-send"
          label="Mark Dispatched"
          size="small"
          severity="success"
          :loading="markingDispatched"
          :disabled="markingDispatched"
          data-testid="action-dispatch"
          @click="onDispatch"
        />
        <Button
          icon="pi pi-comment"
          label="Add Note"
          size="small"
          severity="secondary"
          text
          data-testid="action-add-note"
          @click="noteOpen = true"
        />
      </div>
    </div>

    <!-- Two-column layout -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <!-- Main column -->
      <div class="lg:col-span-2 space-y-5">
        <!-- Applicant details -->
        <div class="bg-white rounded-lg shadow p-5">
          <h4 class="font-semibold text-sm text-gray-700 mb-3">Applicant Details</h4>
          <dl class="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div><dt class="text-gray-500 text-xs">Email</dt><dd class="font-medium">{{ request.applicant_email }}</dd></div>
            <div><dt class="text-gray-500 text-xs">Phone</dt><dd>{{ request.applicant_phone || '—' }}</dd></div>
            <div><dt class="text-gray-500 text-xs">Matric No</dt><dd>{{ request.matric_no || '—' }}</dd></div>
            <div><dt class="text-gray-500 text-xs">Applicant Category</dt><dd>{{ formatCat(request.applicant_category) }}</dd></div>
            <div>
              <dt class="text-gray-500 text-xs">Academic Status</dt>
              <dd><Tag :value="formatStatus(request.student_status)" :severity="academicStatusSeverity" /></dd>
            </div>
            <div><dt class="text-gray-500 text-xs">Programme</dt><dd>{{ request.programme_name || '—' }}</dd></div>
            <div><dt class="text-gray-500 text-xs">Year of Admission</dt><dd>{{ request.year_of_admission || '—' }}</dd></div>
            <div><dt class="text-gray-500 text-xs">Year of Graduation</dt><dd>{{ request.year_of_graduation || '—' }}</dd></div>
            <div><dt class="text-gray-500 text-xs">Number of Copies</dt><dd>{{ request.number_of_copies }}</dd></div>
            <div>
              <dt class="text-gray-500 text-xs">Supporting Document</dt>
              <dd v-if="supportingDocumentName">
                <a
                  v-if="request.supporting_document_url"
                  :href="request.supporting_document_url"
                  target="_blank"
                  rel="noopener"
                  class="text-primary hover:underline break-all"
                  data-testid="transcript-supporting-document"
                ><i class="pi pi-download text-xs mr-1"></i>{{ supportingDocumentName }}</a>
                <span v-else class="break-all" data-testid="transcript-supporting-document">{{ supportingDocumentName }}</span>
              </dd>
              <dd v-else class="text-gray-400">No supporting document</dd>
            </div>
          </dl>

          <div v-if="hasDelivery" class="mt-4 pt-4 border-t">
            <h5 class="text-xs font-semibold text-gray-500 mb-2 uppercase">Delivery</h5>
            <dl class="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div v-if="request.destination_institution"><dt class="text-gray-500 text-xs">Institution</dt><dd>{{ request.destination_institution }}</dd></div>
              <div v-if="request.recipient_email"><dt class="text-gray-500 text-xs">Recipient Email</dt><dd>{{ request.recipient_email }}</dd></div>
              <div v-if="request.destination_address" class="md:col-span-2">
                <dt class="text-gray-500 text-xs">Destination Address</dt>
                <dd class="whitespace-pre-line">{{ request.destination_address }}</dd>
              </div>
              <div v-if="request.courier_name"><dt class="text-gray-500 text-xs">Courier</dt><dd>{{ request.courier_name }}</dd></div>
              <div v-if="request.courier_phone"><dt class="text-gray-500 text-xs">Courier Phone</dt><dd>{{ request.courier_phone }}</dd></div>
              <div v-if="request.courier_address" class="md:col-span-2">
                <dt class="text-gray-500 text-xs">Courier Address</dt>
                <dd class="whitespace-pre-line">{{ request.courier_address }}</dd>
              </div>
              <div v-if="request.platform_name"><dt class="text-gray-500 text-xs">Credential Platform</dt><dd>{{ request.platform_name }}</dd></div>
              <div v-if="request.platform_code"><dt class="text-gray-500 text-xs">Platform Code</dt><dd class="font-mono">{{ request.platform_code }}</dd></div>
            </dl>
          </div>

          <div v-if="request.complaint_note" class="mt-4 pt-4 border-t">
            <h5 class="text-xs font-semibold text-gray-500 mb-2 uppercase">Complaint Note</h5>
            <p class="text-sm whitespace-pre-line">{{ request.complaint_note }}</p>
          </div>

          <div v-if="request.rejection_reason" class="mt-4 pt-4 border-t">
            <h5 class="text-xs font-semibold text-red-600 mb-2 uppercase">Rejection Reason</h5>
            <p class="text-sm whitespace-pre-line text-red-700">{{ request.rejection_reason }}</p>
          </div>
        </div>

        <!-- Transcript data review -->
        <div class="bg-white rounded-lg shadow p-5">
          <div class="flex items-center justify-between mb-3">
            <h4 class="font-semibold text-sm text-gray-700">Transcript Data</h4>
            <span v-if="generatedTranscript" class="text-xs text-gray-400">
              v{{ generatedTranscript.version }} · {{ formatDateTime(generatedTranscript.generated_at) }}
            </span>
          </div>
          <TranscriptDataReviewPanel
            :data="generatedTranscript?.data"
            :coverLetter="generatedTranscript?.cover_letter"
          />
        </div>
      </div>

      <!-- Sidebar -->
      <div class="space-y-5">
        <!-- Bill -->
        <div v-if="request.bill" class="bg-white rounded-lg shadow p-5">
          <h4 class="font-semibold text-sm text-gray-700 mb-3">Payment</h4>
          <dl class="text-sm space-y-1">
            <div class="flex justify-between">
              <dt class="text-gray-500">Amount</dt>
              <dd class="font-bold">{{ formatCurrency(request.bill.net_amount ?? request.bill.amount) }}</dd>
            </div>
            <div class="flex justify-between"><dt class="text-gray-500">Paid</dt><dd>{{ formatCurrency(request.bill.amount_paid ?? 0) }}</dd></div>
            <div class="flex justify-between"><dt class="text-gray-500">Balance</dt><dd>{{ formatCurrency(request.bill.balance ?? 0) }}</dd></div>
            <div class="flex justify-between"><dt class="text-gray-500">Status</dt><dd><Tag :value="request.bill.status" /></dd></div>
            <div v-if="request.bill.due_date" class="flex justify-between"><dt class="text-gray-500">Due Date</dt><dd>{{ formatDate(request.bill.due_date) }}</dd></div>
          </dl>
        </div>

        <!-- Activity timeline -->
        <div class="bg-white rounded-lg shadow p-5">
          <h4 class="font-semibold text-sm text-gray-700 mb-3">Activity</h4>
          <TranscriptActivityTimeline :activities="request.activities" />
        </div>
      </div>
    </div>

    <!-- Dialogs -->
    <RejectTranscriptDialog v-model="rejectOpen" :requestId="request.id" />
    <AddTranscriptNoteDialog v-model="noteOpen" :requestId="request.id" />
    <LinkTranscriptStudentDialog v-model="linkOpen" :requestId="request.id" />
    <CoverLetterEditorDialog
      v-model="coverOpen"
      :generatedTranscriptId="generatedTranscript?.id ?? null"
      :initialCoverLetter="generatedTranscript?.cover_letter"
      :requestId="request.id"
    />
  </div>
</template>

<script setup lang="ts">
import { useConfirm } from 'primevue/useconfirm';
import {
  TranscriptRequestStatusEnum,
  TranscriptTypeCategoryEnum,
} from '@/types/composition-functions';
import { useTranscriptRequest } from '@/composables/transcript/useTranscriptRequests';
import { useTranscriptMutations } from '@/composables/transcript/useTranscriptMutations';
import { formatCurrency } from '@/utils/currency';

const props = defineProps<{ requestId: string | null }>();

const requestIdRef = computed(() => props.requestId ?? undefined);
const { request, loading } = useTranscriptRequest(requestIdRef);

// The stored path is a storage key, not something to show. The upload's own
// filename identifies it; a request filed before that was kept gets a generic label.
const supportingDocumentName = computed(() => {
  const r = request.value;
  if (!r) return null;
  if (r.supporting_document_name) return r.supporting_document_name;
  return r.supporting_document_path ? 'Supporting document' : null;
});

const {
  approveRequest, approving,
  regenerateData, regenerating,
  markRequestDispatched, markingDispatched,
} = useTranscriptMutations();

const confirm = useConfirm();

const rejectOpen = ref(false);
const noteOpen = ref(false);
const linkOpen = ref(false);
const coverOpen = ref(false);

const generatedTranscript = computed(() => request.value?.generatedTranscript ?? null);
const hasGeneratedData = computed(() => !!generatedTranscript.value);

const status = computed(() => request.value?.status);

const canLinkStudent = computed(() =>
  !!request.value
  && !request.value.student_id
  && status.value !== TranscriptRequestStatusEnum.Rejected
  && status.value !== TranscriptRequestStatusEnum.Collected,
);

const canRegenerate = computed(() =>
  !!request.value?.student_id
  && status.value !== TranscriptRequestStatusEnum.Collected
  && status.value !== TranscriptRequestStatusEnum.Rejected,
);

const canApprove = computed(() => status.value === TranscriptRequestStatusEnum.Processing);
const canReject = computed(() => status.value === TranscriptRequestStatusEnum.Processing);

const canDispatch = computed(() =>
  status.value === TranscriptRequestStatusEnum.Approved
  && request.value?.transcriptType?.category === TranscriptTypeCategoryEnum.OfficialHardCopy,
);

const hasDelivery = computed(() =>
  !!(
    request.value?.destination_institution
    || request.value?.destination_address
    || request.value?.recipient_email
    || request.value?.courier_name
    || request.value?.courier_address
    || request.value?.platform_name
    || request.value?.platform_code
  ),
);

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const formatStatus = (s?: string | null): string => {
  if (!s) return '—';
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatCat = (c?: string | null): string => {
  if (!c) return '—';
  switch (c) {
  case 'current_student': return 'Current Student';
  case 'unverified_applicant': return 'Without Matric No';
  case 'matric_retrieved': return 'With Matric No';
  default: return c.replace(/_/g, ' ').replace(/\b\w/g, (c2) => c2.toUpperCase());
  }
};

const academicStatusSeverity = computed<'success' | 'info' | 'warn' | 'danger' | 'secondary'>(() => {
  switch ((request.value?.student_status ?? '').toLowerCase()) {
  case 'graduated': return 'success';
  case 'active': return 'info';
  case 'withdrew': return 'warn';
  case 'rusticated':
  case 'expelled': return 'danger';
  default: return 'secondary';
  }
});

const onApprove = () => {
  if (!request.value) return;
  const id = request.value.id;
  confirm.require({
    message: 'Approve this transcript request? The applicant will be notified by email.',
    header: 'Approve Request',
    icon: 'pi pi-check-circle',
    acceptClass: 'p-button-success',
    accept: async() => {
      try { await approveRequest(id); } catch { /* toast shown by composable */ }
    },
  });
};

const onRegenerate = () => {
  if (!request.value) return;
  const id = request.value.id;
  confirm.require({
    message: 'Re-generate transcript data for this request? The current generated data will be replaced.',
    header: 'Regenerate Data',
    icon: 'pi pi-refresh',
    accept: async() => {
      try { await regenerateData(id); } catch { /* */ }
    },
  });
};

const onDispatch = () => {
  if (!request.value) return;
  const id = request.value.id;
  confirm.require({
    message: 'Mark this hard-copy transcript as dispatched/collected?',
    header: 'Mark Dispatched',
    icon: 'pi pi-send',
    acceptClass: 'p-button-success',
    accept: async() => {
      try { await markRequestDispatched(id); } catch { /* */ }
    },
  });
};
</script>
